import { ServerAuthorizationTokenResponse } from '@azure/msal-common/dist/response/ServerAuthorizationTokenResponse'
import { AxiosResponse } from 'axios'
import {
    AUTHENTICATION_SCOPES,
    AuthenticationPhase,
    Host,
    IdentityStateAction,
    PersistentIdentityContextProps,
    SecurityAuthority,
    WebPubSubToken,
} from '../../context/IdentityContext'
import { Destination, graphBaseUrl } from '../../context/AxiosContext'
import useAxios from '../../hooks/useAxios'
import { PublicClientApplication } from '@azure/msal-browser'
import { Dispatch, ReactElement, useState } from 'react'
import useAcquireBootstrapToken from './useAcquireBootstrapToken'
import { useDispatch } from 'react-redux'
import { AppAction } from '../../config/appConfig'
import { isOfficeInitialized } from '../../helpers/outlookHelper'
import { DateTime } from 'luxon'
import { parseDateTime } from '../../helpers/dateHelper'

export type IdentityMethods = {
    getBaseUrl: (destination: Destination) => string
    getToken: (authority: SecurityAuthority) => ServerAuthorizationTokenResponse | WebPubSubToken
    acquireToken: (authority: SecurityAuthority) => Promise<ServerAuthorizationTokenResponse>
    selectAccount: (username: string) => void
    selectHost: (host: Host) => void
    selectMailbox: (mailbox: string) => void
    clearError: () => void
    login: (redirect?: boolean) => void // Redirect to the Azure authentication service.
    logout: (action?: AppAction.Reset | AppAction.LogOut, username?: string, msal?: boolean) => void
}

type Props = {
    state: PersistentIdentityContextProps
    bootstrapToken: string
    handleBootstrapToken: (token?: string) => void
    pca: PublicClientApplication
    dispatch: Dispatch<IdentityStateAction>
    setPhase: (phase: AuthenticationPhase) => void
    setError: (error: ReactElement | string) => void
}

export default function useIdentityMethods(): (props: Props) => IdentityMethods {
    const axios = useAxios()
    const reduxDispatch = useDispatch()
    const [tokenRequests, setTokenRequests] = useState<{
        [key: string]: Promise<ServerAuthorizationTokenResponse> | undefined
    }>({})

    const acquireBootstrapToken = useAcquireBootstrapToken()

    return ({
        state,
        bootstrapToken,
        handleBootstrapToken,
        pca,
        dispatch,
        setPhase,
        setError,
    }: Props): IdentityMethods => ({
        getBaseUrl: (destination: Destination = Destination.Hammock): string => {
            if (!state.currentHost) {
                return undefined
            }

            switch (destination) {
                case Destination.SharePoint:
                case Destination.Exchange:
                    return graphBaseUrl
                case Destination.Documents:
                    return `${graphBaseUrl}/drives/${state.currentHost?.DriveId}`
                case Destination.ReplicationEmail:
                    return `${graphBaseUrl}/users/${state.currentHost?.ReplicationMail}`
                case Destination.Email:
                    return `${graphBaseUrl}/${state.mailbox ? 'users/' + state.mailbox : 'me'}`
                case Destination.PrivateEmail:
                    return `${graphBaseUrl}/me`
                case Destination.Hammock:
                default:
                    return state.currentHost?.Backend
            }
        },
        getToken: (authority: SecurityAuthority) => state.tokens?.[authority],
        acquireToken: (authority: SecurityAuthority) => {
            // If there's an ongoing request, return it.
            if (tokenRequests?.[authority]) {
                return tokenRequests[authority]
            }

            const redeemBootstrapToken = (bootstrapToken: string) => {
                if (!bootstrapToken || !state.currentHost) {
                    throw new Error('No bootstrap token has been set.')
                }
                if (!state.currentHost?.RowKey) {
                    throw new Error('No host has been set.')
                }

                console.log(`Fetch new token token for authority ${authority}.`)

                return axios.get<ServerAuthorizationTokenResponse>(
                    `/api/token/${state.currentHost.RowKey}/${authority}`,
                    {
                        baseURL: process.env.ROUTER_HOST,
                        headers: { Authorization: `Bearer ${bootstrapToken}`, SuppressErrors: 'true' },
                    },
                )
            }

            const request = Promise.reject()
                // Test to use the bootstrap token.
                .catch(() => {
                    return redeemBootstrapToken(bootstrapToken)
                })
                // If the bootstrap token also fails, we should acquire a new bootstrap token.
                .catch(() =>
                    acquireBootstrapToken(state.username, pca, handleBootstrapToken).then(redeemBootstrapToken),
                )
                .then((response: AxiosResponse<ServerAuthorizationTokenResponse>) => {
                    //Check if the response contains no error.
                    console.log(response?.data)
                    if (response?.data && response?.data?.error === undefined) {
                        console.log(
                            `Received token for authority ${authority}, expires at ` +
                                (response.data?.expires_in
                                    ? DateTime.now().plus({ seconds: response.data?.expires_in })
                                    : parseDateTime(response.data?.['expires_at'])
                                ).toString(),
                        )
                        dispatch({
                            type: 'tokens',
                            payload: {
                                [authority]: response.data,
                            },
                        })
                        return response.data
                    }
                    throw new Error(response?.data?.error)
                })
                .catch(error => {
                    console.log(`Clear tokens for authority ${authority}, error ${error}.`)
                    // Clear the token that failed from memory.
                    dispatch({
                        type: 'tokens',
                        payload: {
                            [authority]: undefined,
                        },
                    })

                    // Re-throw the error so it can be caught downstream.
                    throw error
                })
                .finally(() => {
                    // Remove the promise once done.
                    setTokenRequests(prev => ({ ...prev, [authority]: undefined }))
                })

            setTokenRequests(prev => ({ ...prev, [authority]: request }))

            return request
        },
        selectAccount: (username: string) => {
            dispatch({ type: 'username', payload: username.toLowerCase() })
            setPhase(AuthenticationPhase.FetchBootstrapToken)
        },
        selectHost: (host: Host) => {
            dispatch({ type: 'currentHost', payload: host })
            setPhase(AuthenticationPhase.FetchTokens)
        },
        clearError: () => setError(undefined),
        login: (redirect?: boolean) => {
            if (redirect) {
                if (isOfficeInitialized()) {
                    console.log(`Get new bootstrap token for user from outlook.`)
                    Office.auth
                        .getAccessToken({ allowConsentPrompt: true, allowSignInPrompt: true })
                        .then(handleBootstrapToken)
                        .catch(e => console.log(e))
                } else if (pca) {
                    console.log(`Redirect user for new bootstrap token.`)
                    // Redirect the user to the Microsoft login page.
                    pca.acquireTokenRedirect({
                        scopes: AUTHENTICATION_SCOPES,
                        prompt: 'select_account',
                    })
                        .then(() => '')
                        .catch(e => console.log(e))
                } else {
                    setError('De autorisatiebibliotheek is niet geladen.')
                }
            }
            setPhase(AuthenticationPhase.SelectAccount)
        },
        logout: (action?: AppAction.Reset | AppAction.LogOut, username?: string, msal?: boolean) => {
            reduxDispatch({ type: action || AppAction.LogOut })

            if (action === AppAction.Reset) {
                // If we want to reset, we remove everything that we may have stored in memory.
                dispatch({
                    type: 'username',
                    payload: undefined,
                })
                window.location.reload()
            } else if (username) {
                // If we want to log out a specific user, it should also trigger a state reset if this user is the
                // current user.
                if (username === state.username) {
                    dispatch({
                        type: 'username',
                        payload: undefined,
                    })
                    setPhase(AuthenticationPhase.SelectAccount)
                }
                // Log the user also out from MSAL.
                if (msal && pca) {
                    pca.logoutRedirect({
                        account: pca
                            .getAllAccounts()
                            .find(
                                account =>
                                    account.username.toLowerCase() === (username || state.username).toLowerCase(),
                            ),
                        onRedirectNavigate: () => false,
                    })
                        .then(() => '')
                        .catch(e => console.log(e))
                }
            } else {
                // If no username is provided, we just want to switch host.
                dispatch({
                    type: 'currentHost',
                    payload: undefined,
                })
                setPhase(AuthenticationPhase.SelectHost)
            }
        },
        selectMailbox: (mailbox: string) => {
            console.log(`Detected mailbox change: ${mailbox}.`)
            dispatch({ type: 'mailbox', payload: mailbox.toLowerCase() })
        },
    })
}
